/*
=========================================================
* RingOperator Features — Dispatch design
=========================================================
*/

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

import { brandPrimary, brandCard, brandCream } from "pages/RingOperatorHomepage/theme";

const line = "rgba(14,13,11,0.1)";
const muted = "rgba(14,13,11,0.55)";

const items = [
  {
    emoji: "🌙",
    title: "Your Team Gets Their Nights Back",
    body: "No more 2am calls to your personal cell. RingOperator answers every after-hours call, books appointments, and handles questions — so you actually sleep.",
  },
  {
    emoji: "📅",
    title: "Your Calendar Fills While You Sleep",
    body: "Real-time Google Calendar sync means callers get instant confirmation. No hold music, no missed bookings — and if you cancel an appointment, the AI calls the customer to let them know.",
  },
  {
    emoji: "✅",
    title: "No More Double-Booking Headaches",
    body: "AI knows your availability, your rules, and your capacity. It automatically optimizes scheduling and prevents conflicts before they happen.",
  },
  {
    emoji: "⚡",
    title: "Set Up Today. Problem Solved Tomorrow.",
    body: "AI learns your business details instantly from your information, hours, and services. 30 minutes of setup, no IT team needed, no training required.",
  },
  {
    emoji: "🌐",
    title: "Speaks Every Customer's Language",
    body: "Auto-detects the caller's language and responds natively. Mandarin, Spanish, French, Arabic — no more language barrier awkwardness, no more lost customers.",
  },
  {
    emoji: "🎧",
    title: "Review Every Call, Catch Every Complaint",
    body: "Full transcripts and audio recordings of every call. Complaints are captured automatically, classified by severity, and tracked to resolution — critical ones alert you instantly.",
  },
];

function Features() {
  return (
    <MKBox component="section" id="features" className="ro-root" sx={{ py: { xs: 8, md: 12 }, background: brandCream }}>
      <Container>
        <Grid container spacing={5} alignItems="end" sx={{ mb: 6 }}>
          <Grid item xs={12} md={7}>
            <MKTypography
              component="h2"
              sx={{
                m: 0,
                fontSize: { xs: "2.2rem", md: "4.75rem" },
                lineHeight: 0.95,
                letterSpacing: "-0.03em",
                fontWeight: 800,
                color: brandPrimary,
              }}
            >
              Does{" "}
              <MKBox component="span" className="ro-serif" sx={{ fontStyle: "italic" }}>
                everything
              </MKBox>
              <br />
              a great receptionist does.
            </MKTypography>
          </Grid>
          <Grid item xs={12} md={5}>
            <MKTypography sx={{ color: muted, fontSize: { xs: "1rem", md: "1.1rem" }, lineHeight: 1.55, maxWidth: 420 }}>
              Except it doesn't take lunch breaks, doesn't quit, and works for a hundredth of the cost.
            </MKTypography>
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          {items.map((f, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <MKBox
                sx={{
                  background: brandCard,
                  border: `1px solid ${line}`,
                  borderRadius: 3,
                  p: 3.5,
                  minHeight: 220,
                  height: "100%",
                  transition: "all 200ms ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 14px 28px rgba(14,13,11,0.08)",
                  },
                }}
              >
                <MKBox sx={{ fontSize: 32, mb: 2.5 }}>{f.emoji}</MKBox>
                <MKTypography
                  component="h3"
                  sx={{ m: 0, fontSize: "1.25rem", fontWeight: 700, letterSpacing: "-0.01em", color: brandPrimary }}
                >
                  {f.title}
                </MKTypography>
                <MKTypography sx={{ color: muted, fontSize: "0.95rem", lineHeight: 1.55, mt: 1 }}>
                  {f.body}
                </MKTypography>
              </MKBox>
            </Grid>
          ))}
        </Grid>
      </Container>
    </MKBox>
  );
}

export default Features;
